import { useState, useEffect, useRef, useCallback } from "react";

const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/instagram-search`;
const API_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const fetchApi = async (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_BASE}?${qs}`, {
    headers: { "Content-Type": "application/json", apikey: API_KEY },
  });
  return res.json();
};

// Poll the cache-backed endpoint until the job is ready/failed.
// Returns { status, ...payload }
async function pollUntilReady(
  params: Record<string, string>,
  {
    intervalMs = 1500,
    timeoutMs = 60_000,
    signal,
  }: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
) {
  const start = Date.now();
  // Up to ~timeoutMs of polling
  // First request kicks off the job server-side
  while (true) {
    if (signal?.aborted) return { status: "aborted" };
    const json = await fetchApi(params).catch(() => ({ status: "failed" }));
    if (json?.status === "ready" || json?.status === "failed") return json;
    if (Date.now() - start > timeoutMs) return { status: "failed", error: "timeout" };
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

// --- Types ---
export interface IgUser {
  username: string;
  full_name: string;
  profile_pic: string;
  is_verified: boolean;
  followers: number;
}

export interface IgUserDetail extends IgUser {
  is_private: boolean;
  biography: string;
  following: number;
  posts_count: number;
  external_url: string;
}

export interface IgPost {
  id: string;
  shortcode: string;
  type: "Image" | "Video" | "Sidecar" | string;
  cover: string;
  video_url: string;
  url: string;
  caption: string;
  likes: number;
  comments: number;
  views: number;
  timestamp: string;
}

// --- Search Users (debounced) ---
export function useIgSearch(query: string) {
  const [users, setUsers] = useState<IgUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    const trimmed = query.replace(/^@/, "").trim();
    if (trimmed.length < 2) {
      setUsers([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    debounceRef.current = setTimeout(async () => {
      const json = await pollUntilReady(
        { action: "search", keyword: trimmed, count: "10" },
        { intervalMs: 1200, timeoutMs: 45_000, signal: ctrl.signal },
      );
      if (ctrl.signal.aborted) return;
      setUsers(Array.isArray(json?.users) ? json.users : []);
      setLoading(false);
      setSearched(true);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      ctrl.abort();
    };
  }, [query]);

  return { users, loading, searched };
}

// --- User Detail ---
export function useIgUser(username: string) {
  const [user, setUser] = useState<IgUserDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchUser = useCallback(async (uname: string, signal?: AbortSignal) => {
    if (!uname) return;
    setLoading(true);
    const json = await pollUntilReady(
      { action: "user", username: uname },
      { intervalMs: 1500, timeoutMs: 60_000, signal },
    );
    if (signal?.aborted) return;
    setUser(json?.user || null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!username) return;
    const ctrl = new AbortController();
    fetchUser(username, ctrl.signal);
    return () => ctrl.abort();
  }, [username, fetchUser]);

  return { user, loading, refetch: fetchUser };
}

// --- Posts ---
export function useIgPosts(username: string, enabled = true, count = 6) {
  const [posts, setPosts] = useState<IgPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!username || !enabled) {
      setPosts([]);
      setLoading(false);
      setFailed(false);
      return;
    }

    const ctrl = new AbortController();
    setLoading(true);
    setFailed(false);
    pollUntilReady(
      { action: "posts", username, count: String(count) },
      { intervalMs: 2000, timeoutMs: 120_000, signal: ctrl.signal },
    ).then((json) => {
      if (ctrl.signal.aborted) return;
      setPosts(Array.isArray(json?.posts) ? json.posts : []);
      setFailed(json?.status === "failed");
      setLoading(false);
    });
    return () => ctrl.abort();
  }, [username, enabled, count]);

  return { posts, loading, failed };
}
